import memoizeOne from 'memoize-one';
import React, { PureComponent } from 'react';
import Measure from 'react-measure';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';
import { Currency } from '~/Api/Currency';
import { Item as ProductItem, Product } from '~/Api/Product';
import { PricedProduct } from '~/Product';

import Header, { SortFields } from './Header/Header';
import Row from './Row/Row';

const FixedRows = {
	header: { index: 0 },
	headRow: { index: 1 },
	panel: { index: 2 },
	footer: { index: null },
};
const FixedRowsCount = Object.keys(FixedRows).length;
const countRows = memoizeOne((list: any[]) => list.length + FixedRowsCount);
const createRowsData = memoizeOne(
	(
		list: any[],
		accessory: React.ReactNode,
		accessoryHeader: React.ReactNode,
		header: React.ReactNode,
		footer: React.ReactNode
	) => {
		return { list, accessory, accessoryHeader, header, footer };
	}
);

interface Props {
	sortField: string | null;
	sortFields: SortFields | null;
	orderDirection: string | null;
	currency: string | null;
	priceType: string | null;
	filterCalculatedPrice: boolean;
	currencies?: Currency[] | null;
	header: React.ReactNode;
	initialHeaderHeight: number;
	footer: React.ReactNode;
	footerHeight: number;
	headRowHeight: number | null;
	rowHeight: number | null;
	panel: React.ReactNode;
	panelHeight: number;
	accessory?(a: PricedProduct<any>): any;
	accessoryHeader: React.ReactNode;
	priceHeader: React.ReactNode;
	products: PricedProduct<any>[];
	detailLink(b: Product): { pathname: string; search: string };
	sortLink(a: string, b: string): { pathname: string; search: string };
	packageCell?(product: PricedProduct<ProductItem>, index: number): React.ReactNode;
	isFetching: boolean;
	highlightProductId: string | null;
	headRowRef: { current: Header | null } | null;
	onPriceChange(a: string | null, b: string | null, c: boolean): void;
	checkedRows?: Product[];
	checkRow?(product: Product, checked: boolean): void;
}

interface State {
	headerHeight: number | null;
}

export default class ProductsTable extends PureComponent<Props, State> {
	static defaultProps = {
		sortField: null,
		orderDirection: null,
		currency: null,
		priceType: null,
		accessoryHeader: null,
		headRowHeight: 58,
		rowHeight: 50,
		panel: null,
		panelHeight: 30,
		sortLink: null,
		highlightProductId: null,
		headRowRef: null,
	};

	state = {
		headerHeight: null,
	};

	pricePopoverContainerElement = null;

	listElement = React.createRef<VariableSizeList>();

	get rowsCount(): number {
		return countRows(this.props.products);
	}

	get rowsData(): any {
		const { products, accessory, accessoryHeader, header, footer } = this.props;
		return createRowsData(products, accessory, accessoryHeader, header, footer);
	}

	componentDidUpdate(prevProps: Props, prevState: State) {
		const listElement = this.listElement.current;
		if (listElement === null) {
			return;
		}
		if (prevState.headerHeight !== this.state.headerHeight || prevProps.footerHeight !== this.props.footerHeight) {
			listElement.resetAfterIndex(0, true);
		} else if (prevProps.panel !== this.props.panel) {
			listElement.resetAfterIndex(FixedRows.panel.index - 1, true);
		} else if (prevProps.products.length !== this.props.products.length) {
			listElement.resetAfterIndex(FixedRowsCount + prevProps.products.length - 1, true);
		}
	}

	render() {
		return (
			<AutoSizer>
				{({ width, height }: { width: number; height: number }) => (
					<VariableSizeList
						height={height}
						itemCount={this.rowsCount}
						itemsData={this.rowsData}
						itemSize={this.resolveRowHeight}
						ref={this.listElement}
						width={width}
					>
						{/* @ts-expect-error: wrong typing of VariableSizeList */}
						{this.renderRow}
					</VariableSizeList>
				)}
			</AutoSizer>
		);
	}

	resolveRowHeight = (index: number): number => {
		if (index === FixedRows.header.index) {
			return this.state.headerHeight ?? this.props.initialHeaderHeight;
		}
		if (index === FixedRows.headRow.index) {
			return this.props.headRowHeight || 0;
		}

		if (index === FixedRows.panel.index) {
			return this.props.panel ? this.props.panelHeight : 0;
		}

		if (index === this.rowsCount - 1) {
			return this.props.footerHeight;
		}
		return this.props.rowHeight || 0;
	};

	renderRow: ({ index, style }: { index: number; style: any }) => React.ReactNode = ({ index, style }) => {
		if (index === FixedRows.header.index) {
			return (
				<div style={style}>
					<Measure bounds onResize={(contentRect) => this.setState({ headerHeight: contentRect?.bounds?.height || 0 })}>
						{({ measureRef }) => <div ref={measureRef}>{this.props.header}</div>}
					</Measure>
				</div>
			);
		}
		if (index === FixedRows.headRow.index) {
			return this.props.products.length === 0 ? null : this.renderHeader(style);
		}

		if (index === FixedRows.panel.index) {
			return this.props.products.length === 0 ? null : <div style={style}>{this.props.panel}</div>;
		}

		if (index === this.rowsCount - 1) {
			return <div style={style}>{this.props.footer}</div>;
		}

		const { products, highlightProductId, priceType, accessory, detailLink, packageCell, isFetching, checkRow } =
			this.props;
		const productIndex = index - 3;
		const pricedProduct = products[productIndex];
		const { product } = pricedProduct;
		const checked = Boolean(this.props.checkedRows?.some((row) => row.id === product.id));

		return (
			<Row
				accessory={accessory}
				detailLink={detailLink}
				highlightProductId={highlightProductId}
				checked={checked}
				checkRow={(value) => checkRow?.(product, value)}
				index={productIndex}
				isFetching={isFetching}
				packageCell={packageCell}
				pricedProduct={pricedProduct}
				priceType={priceType}
				style={style}
			/>
		);
	};

	renderHeader(style: any) {
		const {
			props: {
				sortField,
				sortFields,
				orderDirection,
				sortLink,
				accessoryHeader,
				priceHeader,
				currency,
				currencies,
				priceType,
				filterCalculatedPrice,
				headRowRef,
				onPriceChange,
			},
			state: { headerHeight },
		} = this;

		return (
			<Header
				accessoryHeader={accessoryHeader}
				currencies={currencies || null}
				currency={currency}
				filterCalculatedPrice={filterCalculatedPrice}
				orderDirection={orderDirection}
				priceHeader={priceHeader ? React.cloneElement(priceHeader as React.ReactElement, { headerHeight }) : null}
				priceType={priceType} // table header and price header must be rerendered when header height changes
				ref={headRowRef}
				sortField={sortField}
				sortFields={sortFields}
				sortLink={sortLink}
				style={style}
				onPriceChange={onPriceChange}
			/>
		);
	}
}
